/**
 * CargoSkyModule — the sky trade lane, rendered (2026-08-28):
 *
 *   FREIGHTERS  every `cargo_convoy` row within range flies its server row —
 *               position is a pure function of the row + wall clock
 *               (lib/cargo_route.ts), so every client sees the same convoy in
 *               the same place. Hull `prop:ship_bulwark` (courier fallback),
 *               THREE escort fighters in a delta (port/starboard/trail-high).
 *   PIRACY      the freighter and escorts register in the air_targets
 *               registry — the player's ship cannons (AtlasVehicleModule's
 *               bolt loop) can hit them. Fire on the convoy and the escorts
 *               BREAK FORMATION and hunt the player (return fire via the
 *               'atlas:ship-damage' bus). Kill the freighter → the server
 *               (`pirateCargoConvoy`) crashes it at a server-computed point,
 *               brands the pirate with the EXPORTING town (standing floored),
 *               and every client plays the same fall.
 *   SALVAGE     beside the wreck ON FOOT: an E prompt → `lootCargoWreck`
 *               fences the manifest (resource stacks / coins).
 *
 * Doctrine: master-cloned + markShared hulls; one CombatVfx (FX_CAP-bounded,
 * root re-attached across scene swaps); no per-frame allocations; rows are
 * few (≤ ~36 world-wide) so a full iter() per second is nothing.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase, Sub } from '../engine-compat.js';
import { prefabAssetId } from '../lib/player_models.js';
import { markShared } from '../lib/defer_dispose.js';
import { groundYIn } from '../lib/grounding.js';
import { sfx, sfxAt, inverseDistanceGain, arrivalDelayS } from '../lib/sfx.js';
import { reportCraft } from '../lib/audio/craft_feed.js';
import { cargoPose, escortPose, ESCORT_COUNT, type CargoRouteRow } from '../lib/cargo_route.js';
import { airH } from '../lib/air_choreo.js';
import {
  upsertAirTarget, removeAirTarget, airTargetById, sideHostile,
} from '../lib/air_targets.js';
import { CombatVfx } from './living-events/CombatVfx.js';
import type { AssetModule } from './AssetModule.js';

const RENDER_RANGE_M = 4500;
const DROP_RANGE_M = 5200;
const CARGO_LEN_M = 26;
const ESCORT_LEN_M = 9;
const CARGO_HP = 220;
const ESCORT_HP = 36;
const CRASH_ANIM_S = 4.5;
const LOOT_RANGE_M = 42;
const MAX_RENDERED = 3;

/** Analytic ground under a convoy body (slot 0 = hull, 1.. = escorts), resampled after 8 m of travel — 4 stampedHeight evaluations per sample outside the chunk ring (2026-09-12). */
function groundCached(c: Rendered, slot: number, conn: Parameters<typeof groundYIn>[0], wid: Parameters<typeof groundYIn>[1], x: number, z: number): number {
  const cache = (c.gCache ??= []);
  const g = cache[slot];
  if (g && Math.hypot(x - g.x, z - g.z) <= 8) return g.y;
  const y = groundYIn(conn, wid, x, z);
  cache[slot] = { x, z, y };
  return y;
}

interface ConvoyRow {
  convoyId: bigint; worldId: bigint; fromSid: bigint; toSid: bigint;
  commodity: string; qty: number;
  fromX: number; fromZ: number; toX: number; toZ: number;
  departAt: { microsSinceUnixEpoch: bigint }; arriveAt: { microsSinceUnixEpoch: bigint };
  status: string; crashX: number; crashZ: number;
  downedBy: { toHexString(): string };
}

interface Rendered {
  row: ConvoyRow;
  cargo: THREE.Object3D | null;
  escorts: Array<THREE.Object3D | null>;
  escortAlive: boolean[];
  gCache?: Array<{ x: number; z: number; y: number } | undefined>;
  escortFireCd: number[];
  /** Crash animation start (wall s) once status turns 'downed'; 0 = not started. */
  crashT0: number;
  crashFrom: { x: number; y: number; z: number } | null;
  crashDone: boolean;
  wreck: THREE.Object3D | null;
  brandToasted: boolean;
}

const routeOf = (r: ConvoyRow): CargoRouteRow => ({
  fromX: r.fromX, fromZ: r.fromZ, toX: r.toX, toZ: r.toZ,
  departS: Number(r.departAt.microsSinceUnixEpoch) / 1e6,
  arriveS: Number(r.arriveAt.microsSinceUnixEpoch) / 1e6,
});

export class CargoSkyModule implements IModule {
  readonly name = 'CargoSky';
  private app: App | null = null;
  private assets: AssetModule;
  private vfx: CombatVfx | null = null;
  private masters: Partial<Record<'cargo' | 'ftra' | 'ftrb', THREE.Object3D>> = {};
  private loadTried = false;
  private subbed = false;
  private readonly rendered = new Map<string, Rendered>();
  private scanAcc = 0;
  private lootPrompt: HTMLDivElement | null = null;
  private lootConvoy: bigint | null = null;
  private looting = false;
  private readonly _v1 = new THREE.Vector3();
  private readonly _v2 = new THREE.Vector3();

  constructor(assets: AssetModule) { this.assets = assets; }

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('CargoSky', Phase.Animation, 43, (dt) => this.frame(dt), Sub.Update));
    window.addEventListener('keydown', this.onKeyE);
    (globalThis as Record<string, unknown>).__cargoSky = () => ({
      rows: this.rows().map((r) => ({ id: r.convoyId.toString(), status: r.status, from: r.fromSid.toString(), to: r.toSid.toString(), commodity: r.commodity })),
      rendered: [...this.rendered.keys()],
      hostile: [...this.rendered.values()].filter((c) => sideHostile(c.row.convoyId, 'a')).map((c) => c.row.convoyId.toString()),
      lootConvoy: this.lootConvoy?.toString() ?? null,
    });
  }

  private rows(): ConvoyRow[] {
    const conn = atlas.conn;
    if (!conn) return [];
    try { return [...(conn.db as unknown as { cargoConvoy: { iter(): Iterable<ConvoyRow> } }).cargoConvoy.iter()]; } catch { return []; }
  }

  private ensureSub(): void {
    const conn = atlas.conn;
    if (this.subbed || !conn) return;
    this.subbed = true;
    try { conn.subscriptionBuilder().subscribe(['SELECT * FROM cargo_convoy']); } catch { this.subbed = false; }
  }

  private ensureMasters(): void {
    if (this.loadTried || !atlas.conn) return;
    const conn = atlas.conn;
    const want: Array<{ key: 'cargo' | 'ftra' | 'ftrb'; prefab: string; fallback: string }> = [
      { key: 'cargo', prefab: 'prop:ship_bulwark', fallback: 'prop:ship_courier' },
      { key: 'ftra', prefab: 'prop:war_fighter_a', fallback: 'prop:ship_fighter' },
      { key: 'ftrb', prefab: 'prop:war_fighter_b', fallback: 'prop:ship_fighter' },
    ];
    const ids = want.map((w) => prefabAssetId(conn, w.prefab) ?? prefabAssetId(conn, w.fallback));
    if (ids.every((id) => id === null)) return;
    this.loadTried = true;
    want.forEach((w, i) => {
      const id = ids[i];
      if (id === null) return;
      void this.assets.load(id).then((m) => { markShared(m); this.masters[w.key] = m; }).catch(() => { /* slot flies unrendered */ });
    });
  }

  private ensureVfx(): CombatVfx | null {
    const scene = this.app?.world.scene;
    if (!scene) return null;
    if (!this.vfx) this.vfx = new CombatVfx();
    if (this.vfx.root.parent !== scene) scene.add(this.vfx.root);
    return this.vfx;
  }

  private fit(obj: THREE.Object3D, len: number): void {
    const box = new THREE.Box3().setFromObject(obj);
    const s = new THREE.Vector3(); box.getSize(s);
    obj.scale.setScalar(len / Math.max(s.x, s.y, s.z, 0.001));
  }

  private hull(kind: 'cargo' | 'ftra' | 'ftrb', len: number): THREE.Object3D | null {
    const master = this.masters[kind];
    const scene = this.app?.world.scene;
    if (!master || !scene) return null;
    const clone = master.clone();
    this.fit(clone, len);
    scene.add(clone);
    return clone;
  }

  private release(c: Rendered): void {
    const scene = this.app?.world.scene;
    if (c.cargo) scene?.remove(c.cargo);
    for (const e of c.escorts) if (e) scene?.remove(e);
    if (c.wreck) scene?.remove(c.wreck);
    removeAirTarget(`cargo:${c.row.convoyId}:hull`);
    for (let i = 0; i < ESCORT_COUNT; i++) removeAirTarget(`cargo:${c.row.convoyId}:esc:${i}`);
  }

  private salvage(convoyId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.looting = true;
    (conn.reducers as unknown as { lootCargoWreck(a: { convoyId: bigint }): Promise<void> })
      .lootCargoWreck({ convoyId })
      .then(() => {
        sfx('sfx_pickup', { gain: 0.6 });
        sfx('sfx_coin_purchase', { gain: 0.4, cooldownMs: 400 });
        bus.emit('atlas:status', { text: '🛠 Wreck salvaged — the manifest is yours' });
      })
      .catch((e) => bus.emit('atlas:status', { text: `salvage refused: ${(e as Error).message}` }))
      .finally(() => { this.looting = false; });
  }

  private setLootPrompt(convoyId: bigint | null): void {
    this.lootConvoy = convoyId;
    if (convoyId === null) {
      if (this.lootPrompt) this.lootPrompt.style.display = 'none';
      return;
    }
    if (!this.lootPrompt) {
      const d = document.createElement('div');
      d.id = 'cargo-loot-prompt';
      d.style.cssText = 'position:fixed;left:50%;bottom:18%;transform:translateX(-50%);padding:6px 14px;background:rgba(10,14,20,.82);color:#ffd98a;font:14px system-ui;border:1px solid #765;border-radius:6px;z-index:30;pointer-events:none';
      d.textContent = 'E — salvage the cargo wreck';
      document.body.appendChild(d);
      this.lootPrompt = d;
    }
    this.lootPrompt.style.display = 'block';
  }

  private frame(dt: number): void {
    this.scanAcc += dt;
    const nowS = Date.now() / 1000;
    const conn = atlas.conn;
    if (this.scanAcc >= 1) {
      this.scanAcc = 0;
      this.ensureSub();
      this.ensureMasters();
      // Adopt rows in range, drop the gone/far.
      const rows = this.rows();
      const seen = new Set<string>();
      // (bug sweep) release anything rendered for ANOTHER world first — the
      // adopt loop skipped out-of-world rows before marking them seen, and the
      // fallback distance test compared coordinates across different worlds.
      for (const [key, c] of [...this.rendered]) {
        if (c.row.worldId !== atlas.currentWorldId) { this.release(c); this.rendered.delete(key); }
      }
      for (const r of rows) {
        if (r.worldId !== atlas.currentWorldId) continue;
        const key = r.convoyId.toString();
        const p = r.status === 'flying' ? cargoPose(routeOf(r), nowS) : { x: r.crashX, z: r.crashZ };
        const d = Math.hypot(p.x - atlas.localX, p.z - atlas.localZ);
        const have = this.rendered.get(key);
        if (have) {
          have.row = r; // adopt live status changes (downed elsewhere, etc.)
          if (d > DROP_RANGE_M) { this.release(have); this.rendered.delete(key); }
          else seen.add(key);
        } else if (d <= RENDER_RANGE_M && this.rendered.size < MAX_RENDERED && r.status !== 'looted') {
          this.rendered.set(key, {
            row: r, cargo: null, escorts: [null, null, null], escortAlive: [true, true, true],
            escortFireCd: [2, 3, 4], crashT0: 0, crashFrom: null, crashDone: r.status === 'downed', wreck: null, brandToasted: true,
          });
          if (r.status === 'flying') this.rendered.get(key)!.brandToasted = false;
          seen.add(key);
        }
      }
      for (const [key, c] of [...this.rendered]) {
        if (!rows.some((r) => r.convoyId.toString() === key)) { this.release(c); this.rendered.delete(key); }
        else if (!seen.has(key) && Math.hypot((c.row.status === 'flying' ? cargoPose(routeOf(c.row), nowS).x : c.row.crashX) - atlas.localX, (c.row.status === 'flying' ? cargoPose(routeOf(c.row), nowS).z : c.row.crashZ) - atlas.localZ) > DROP_RANGE_M) { this.release(c); this.rendered.delete(key); }
      }
    }
    if (this.rendered.size === 0) { this.setLootPrompt(null); this.vfx?.update(dt); return; }
    if (!conn || atlas.currentWorldId === null) return;
    const vfx = this.ensureVfx();
    const wid = atlas.currentWorldId;
    let lootTarget: bigint | null = null;

    for (const c of this.rendered.values()) {
      const r = c.row;
      const seed = `cv${r.convoyId}`;
      const route = routeOf(r);
      if (r.status === 'flying') {
        const p = cargoPose(route, nowS);
        const gy = groundCached(c, 0, conn, wid, p.x, p.z);
        if (!c.cargo) c.cargo = this.hull('cargo', CARGO_LEN_M);
        if (c.cargo) {
          c.cargo.visible = true;
          c.cargo.position.set(p.x, gy + p.alt, p.z);
          reportCraft(`cargo:${r.convoyId}`, 'freighter', p.x, gy + p.alt, p.z, performance.now()); // the hauler's drone, heard from the ground (lib/audio/craft_feed.ts)
          c.cargo.rotation.set(0, Math.atan2(p.hx, p.hz) + Math.PI / 2, 0); // nose −X hulls
        }
        upsertAirTarget({ id: `cargo:${r.convoyId}:hull`, battleId: r.convoyId, side: 'a', kind: 'cargo', x: p.x, y: gy + p.alt, z: p.z, r: CARGO_LEN_M * 0.55, hp: CARGO_HP, hitByPlayer: false , obj: c.cargo });
        const hostile = sideHostile(r.convoyId, 'a');
        // ── Escorts ──
        for (let i = 0; i < ESCORT_COUNT; i++) {
          if (!c.escortAlive[i]) continue;
          const ek = airH(seed + i) < 0.5 ? 'ftra' : 'ftrb';
          if (!c.escorts[i]) c.escorts[i] = this.hull(ek, ESCORT_LEN_M);
          const e = escortPose(route, i, nowS);
          let ex = e.x, ey = groundCached(c, 1 + i, conn, wid, e.x, e.z) + e.alt, ez = e.z;
          const pd = Math.hypot(atlas.localX - p.x, atlas.localZ - p.z);
          if (hostile && atlas.skyFlight && pd < 1400) {
            // BREAK FORMATION: orbit the pirate's ship and shoot back.
            const ang = nowS * 0.6 + i * 2.1;
            ex = atlas.localX + Math.cos(ang) * 90;
            ez = atlas.localZ + Math.sin(ang) * 90;
            ey = atlas.localY + 14 + Math.sin(nowS * 0.8 + i) * 10;
            c.escortFireCd[i] -= dt;
            if (c.escortFireCd[i] <= 0) {
              c.escortFireCd[i] = 2.0 + airH(`${seed}|fc${i}|${Math.floor(nowS)}`) * 1.4;
              vfx?.bolt(this._v1.set(ex, ey, ez), this._v2.set(atlas.localX, atlas.localY, atlas.localZ), 0xff9090);
              sfxAt('sfx_enemy_cannon', { x: ex, y: ey, z: ez }, { gain: 0.32, cooldownMs: 250, emitter: `esc${i}`, caption: 'Escort fire' });
              if (airH(`${seed}|fh${i}|${Math.floor(nowS * 5)}`) < 0.38) bus.emit('atlas:ship-damage', { amount: 5, why: 'escort fire' });
            }
          }
          const obj = c.escorts[i];
          if (obj) {
            obj.visible = true;
            obj.position.set(ex, ey, ez);
            obj.rotation.set(0, (hostile && atlas.skyFlight && pd < 1400) ? Math.atan2(atlas.localX - ex, atlas.localZ - ez) + Math.PI / 2 : Math.atan2(p.hx, p.hz) + Math.PI / 2, 0);
          }
          const id = `cargo:${r.convoyId}:esc:${i}`;
          upsertAirTarget({ id, battleId: r.convoyId, side: 'a', kind: 'escort', x: ex, y: ey, z: ez, r: ESCORT_LEN_M * 0.75, hp: ESCORT_HP, hitByPlayer: false , obj: obj });
          const tgt = airTargetById(id);
          if (tgt && tgt.hp <= 0) {
            c.escortAlive[i] = false;
            if (obj) obj.visible = false;
            removeAirTarget(id);
            if (vfx) vfx.explosion(this._v1.set(ex, ey, ez), 2.0, 0xffcc88);
            sfxAt('sfx_ship_explosion', { x: ex, y: ey, z: ez }, { gain: 0.5, cooldownMs: 150, caption: 'Explosion' });
            sfx('sfx_kill_confirm', { gain: 0.4, cooldownMs: 400 }); // the confirm is the pilot's own HUD, never a place
          }
        }
        // ── The freighter's fate ──
        const hullTgt = airTargetById(`cargo:${r.convoyId}:hull`);
        if (hullTgt && hullTgt.hp <= 0) {
          removeAirTarget(`cargo:${r.convoyId}:hull`);
          const py = c.cargo?.position.y ?? gy + p.alt;
          c.crashFrom = { x: p.x, y: py, z: p.z };
          (conn.reducers as unknown as { pirateCargoConvoy(a: { convoyId: bigint }): Promise<void> })
            .pirateCargoConvoy({ convoyId: r.convoyId })
            .catch(() => { /* out of range/raced — the row stays flying */ });
          {
            // The freighter breaks up WHERE it is; past a few hundred metres the report arrives after the flash.
            const fd = Math.hypot(atlas.localX - p.x, atlas.localY - py, atlas.localZ - p.z);
            sfxAt('sfx_ship_explosion', { x: p.x, y: py, z: p.z }, { gain: 0.8 * inverseDistanceGain(fd, 400, 5000), delayS: Math.min(4, arrivalDelayS(fd)), occlude: false, caption: 'Explosion' });
          }
          if (vfx && c.cargo) vfx.explosion(c.cargo.position, 3.5, 0xffb070);
        }
      } else if (r.status === 'downed') {
        // Every client plays the same fall to the SERVER's crash point.
        for (let i = 0; i < ESCORT_COUNT; i++) { const e = c.escorts[i]; if (e) e.visible = false; removeAirTarget(`cargo:${r.convoyId}:esc:${i}`); }
        removeAirTarget(`cargo:${r.convoyId}:hull`);
        if (!c.brandToasted) {
          c.brandToasted = true;
          if (conn.identity && r.downedBy.toHexString() === conn.identity.toHexString()) {
            bus.emit('atlas:status', { text: `☠ SKY PIRACY — town #${r.fromSid} brands you; their regard for you is ruined` });
          }
        }
        const cgy = groundYIn(conn, wid, r.crashX, r.crashZ);
        if (!c.crashDone) {
          if (c.crashT0 === 0) {
            c.crashT0 = nowS;
            if (!c.crashFrom) { const p = cargoPose(route, nowS); c.crashFrom = { x: p.x, y: groundYIn(conn, wid, p.x, p.z) + p.alt, z: p.z }; }
          }
          const k = Math.min(1, (nowS - c.crashT0) / CRASH_ANIM_S);
          if (!c.cargo) c.cargo = this.hull('cargo', CARGO_LEN_M);
          if (c.cargo && c.crashFrom) {
            c.cargo.visible = true;
            const kk = k * k; // accelerating fall
            c.cargo.position.set(
              c.crashFrom.x + (r.crashX - c.crashFrom.x) * k,
              c.crashFrom.y + (cgy + 1.5 - c.crashFrom.y) * kk,
              c.crashFrom.z + (r.crashZ - c.crashFrom.z) * k,
            );
            c.cargo.rotation.z = k * 0.55;
            c.cargo.rotation.x = k * 0.2;
            if (k >= 1) {
              c.crashDone = true;
              if (vfx) vfx.explosion(this._v1.set(r.crashX, cgy + 2, r.crashZ), 4.5, 0xffa060);
              {
                // Every client plays the same fall — so every client must hear it from where it FELL, at its distance.
                const cd = Math.hypot(atlas.localX - r.crashX, atlas.localY - (cgy + 2), atlas.localZ - r.crashZ);
                sfxAt('sfx_ship_crash', { x: r.crashX, y: cgy + 2, z: r.crashZ }, { gain: 0.8 * inverseDistanceGain(cd, 400, 5000), cooldownMs: 1000, delayS: Math.min(4, arrivalDelayS(cd)), caption: 'Ship crash' });
              }
            }
          } else if (k >= 1) c.crashDone = true;
        } else if (c.cargo) {
          // The wreck: listing where it fell, smoke curling off it.
          c.cargo.visible = true;
          c.cargo.position.set(r.crashX, cgy + 1.2, r.crashZ);
          c.cargo.rotation.set(0.2, airH(seed) * Math.PI * 2, 0.55);
          if (vfx && Math.floor(nowS * 2) % 5 === 0) vfx.impact(this._v1.set(r.crashX, cgy + 4, r.crashZ), 0.7, 0x555555);
        }
        // The salvage prompt (on foot, beside the wreck).
        if (c.crashDone && !atlas.skyFlight && !atlas.spaceFlight
          && Math.hypot(r.crashX - atlas.localX, r.crashZ - atlas.localZ) < LOOT_RANGE_M) lootTarget = r.convoyId;
      } else {
        // looted — the hulk fades from the world (sweep deletes the row soon).
        if (c.cargo) c.cargo.visible = false;
      }
    }
    this.setLootPrompt(lootTarget);
    vfx?.update(dt);
  }

  private onKeyE = (e: KeyboardEvent): void => {
    if (e.code !== 'KeyE' || this.lootConvoy === null || this.looting) return;
    const el = document.activeElement as HTMLElement | null;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
    this.salvage(this.lootConvoy);
  };

  dispose(): void {
    // (bug sweep) the E-key handler + dev global outlived the module.
    window.removeEventListener('keydown', this.onKeyE);
    delete (globalThis as Record<string, unknown>).__cargoSky;
    for (const c of this.rendered.values()) this.release(c);
    this.rendered.clear();
    if (this.vfx) { this.vfx.dispose(); this.vfx = null; }
    this.lootPrompt?.remove();
  }
}
